/**
 * `deepFrozen` / `frozenMap` / `frozenSet` — a published constant, a value a
 * read hands out, or a process-global table, that no caller can write.
 *
 * `deepFrozen(value)` freezes a constant's literal in place and answers it:
 * the value and every plain object and array reachable from it, so a spec
 * table (`PER_TYPE_SPECS`) and the specs in it keep their identity. A class
 * instance, a function, a `Map` or a `Set` inside it is left as it is (a
 * class freezes its own instances where they are shared, as `FacadeKey`
 * does; a collection is built with `frozenMap` / `frozenSet`).
 *
 * `frozenMap` / `frozenSet`: a `Map` or `Set` that no caller can write. `Object.freeze` does
 * not reach a collection's entries (`Object.freeze(new Set()).add(x)` lands),
 * so these refuse `set` / `add`, `delete` and `clear` with a `TypeError` and
 * are frozen besides. They are a `Map` / `Set` to every reader: iteration,
 * `get`, `has`, `size` and `instanceof` answer as the collection they were
 * built from, which they copy.
 */

/** A `Map` whose entries are fixed at construction. */
class FrozenMap<K, V> extends Map<K, V> {
  constructor(entries: Iterable<readonly [K, V]>) {
    super();
    for (const [key, value] of entries) Map.prototype.set.call(this, key, value);
    Object.freeze(this);
  }

  override set(_key: K, _value: V): this {
    throw new TypeError("frozenMap: a frozen map takes no set");
  }

  override delete(_key: K): boolean {
    throw new TypeError("frozenMap: a frozen map takes no delete");
  }

  override clear(): void {
    throw new TypeError("frozenMap: a frozen map takes no clear");
  }
}

/** A `Set` whose members are fixed at construction. */
class FrozenSet<T> extends Set<T> {
  constructor(members: Iterable<T>) {
    super();
    for (const member of members) Set.prototype.add.call(this, member);
    Object.freeze(this);
  }

  override add(_value: T): this {
    throw new TypeError("frozenSet: a frozen set takes no add");
  }

  override delete(_value: T): boolean {
    throw new TypeError("frozenSet: a frozen set takes no delete");
  }

  override clear(): void {
    throw new TypeError("frozenSet: a frozen set takes no clear");
  }
}

/** A frozen copy of `entries` as a map that refuses every write. */
export function frozenMap<K, V>(entries: Iterable<readonly [K, V]>): ReadonlyMap<K, V> {
  return new FrozenMap(entries);
}

/** A frozen copy of `members` as a set that refuses every write. */
export function frozenSet<T>(members: Iterable<T>): ReadonlySet<T> {
  return new FrozenSet(members);
}

/** `value` with every plain object and array reachable from it frozen, in place (see the module note). */
export function deepFrozen<T>(value: T): T {
  freezePlain(value, new WeakSet<object>());
  return value;
}

function freezePlain(value: unknown, seen: WeakSet<object>): void {
  if (typeof value !== "object" || value === null || seen.has(value)) return;
  seen.add(value);
  const proto: unknown = Object.getPrototypeOf(value);
  if (!Array.isArray(value) && proto !== Object.prototype && proto !== null) return;
  Object.freeze(value);
  for (const member of Object.values(value)) freezePlain(member, seen);
}
